import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  AgentWorkbenchPatch,
  AgentWorkSurfaceManifest,
  Brand,
  BrandVoice,
  BrandExtractionResult,
} from "../../../shared/types";

export type BrandDeleteResponse = {
  data:
    | {
        dry_run: true;
        would_delete: {
          pg: Record<string, number>;
          nosql: Record<string, number>;
        };
      }
    | {
        success: true;
        tombstone_id: string;
        deleted: {
          pg: Record<string, number>;
          nosql_status: "pending" | "complete" | "partial" | "failed";
        };
      };
};

export function useBrands() {
  return useQuery({
    queryKey: ["brands"],
    /** Backend returns `{ data: Brand[] }` (see `GET /api/brands`). */
    queryFn: () => api.get<{ data: Brand[] }>("/api/brands"),
  });
}

export function useBrand(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId],
    queryFn: () => api.get<{ id: string; data: Brand }>(`/api/brands/${brandId}`),
    enabled: !!brandId,
  });
}

export function useBrandVoice(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "voice"],
    queryFn: () => api.get<{ id: string; data: BrandVoice }>(`/api/brands/${brandId}/voice`),
    enabled: !!brandId,
  });
}

export function useBrandVoiceWorkbench(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "voice", "workbench"],
    queryFn: () =>
      api.get<{ data: AgentWorkSurfaceManifest }>(`/api/agent-workbench/brand_voice/${brandId}`),
    enabled: !!brandId,
  });
}

export function useProposeBrandVoiceWorkbenchPatch(brandId: string) {
  return useMutation({
    mutationFn: (body: {
      target_section_id?: string | null;
      quick_action_id?: string | null;
      instruction?: string | null;
    }) =>
      api.post<{ data: AgentWorkbenchPatch }>(
        `/api/agent-workbench/brand_voice/${brandId}/propose`,
        body,
      ),
  });
}

export function useApplyBrandVoiceWorkbenchPatch(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AgentWorkbenchPatch) =>
      api.post<{ data: AgentWorkSurfaceManifest }>(
        `/api/agent-workbench/brand_voice/${brandId}/apply`,
        { patch },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands", brandId, "voice", "workbench"] });
      qc.invalidateQueries({ queryKey: ["brands", brandId, "voice"] });
      qc.invalidateQueries({ queryKey: ["voice-profile", brandId] });
      qc.invalidateQueries({ queryKey: ["voice-history", brandId] });
    },
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    // Backend returns { data: Brand, onboarding } — unwrap so callers can read
    // `.id` directly without thinking about the envelope. (The mismatched type
    // here previously dropped the id, which broke onboarding step 2.)
    mutationFn: async (data: Partial<Brand> & { positioning_statement?: string }) => {
      const res = await api.post<{ data: Brand }>("/api/brands", data);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands"] }),
  });
}

export function useUpdateBrand(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Brand>) => api.put<{ id: string }>(`/api/brands/${brandId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["brands", brandId] });
    },
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, confirm = false }: { brandId: string; confirm?: boolean }) =>
      api.delete<BrandDeleteResponse>(
        `/api/brands/${encodeURIComponent(brandId)}${confirm ? "?confirm=true" : ""}`,
      ),
    onSuccess: async (response, { brandId }) => {
      if ("dry_run" in response.data) return;
      qc.removeQueries({
        predicate: (query) => query.queryKey.some((part) => part === brandId),
      });
      await qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useExtractBrand() {
  return useMutation({
    // The API wraps the result as `{ data: BrandExtractionResult }`; unwrap it so
    // callers receive the profile directly (otherwise every field reads undefined).
    mutationFn: async (url: string) =>
      (await api.post<{ data: BrandExtractionResult }>("/api/brands/extract", { url })).data,
  });
}

export function useUpdateBrandVoice(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (voice: Partial<BrandVoice>) =>
      api.put<{ id: string }>(`/api/brands/${brandId}/voice`, voice),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brands", brandId, "voice"] }),
  });
}

export function useAddToCorpus(brandId: string) {
  return useMutation({
    mutationFn: (data: { content: string; metadata?: Record<string, unknown> }) =>
      api.post<{ data: { id: string } }>(`/api/brands/${brandId}/corpus`, data),
  });
}
